import { useTableActionContext } from '@aibees/elephant/context';
import { useBootstrapLabels } from '@aibees/elephant/hooks/bootstrap/use-bootstrap-labels';
import { SearchType } from '@aibees/elephant/types';
import { Label, LabelGrouping } from '@aibees/elephant/types/label';
import { Scrollbar } from '@aibees/shared/components';
import { PresetVisibility } from '@aibees/shared/definition';
import { classnames } from '@aibees/shared/utils';
import { CardContent, Stack, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { VisibilityTabs } from '../export-dialog/components/visibility-tabs';
import {
  useFilterLabelsByOwnershipTab,
  useOwnershipTabCounts,
} from '@aibees/elephant/hooks/labels/use-label-ownership-tabs';
import { useLabelManagementContext } from '../search-table-action-dialog-lists/context';
import { LabelsListItem } from './components/labels-list-item';
import { useLabelsEmptyState } from './hooks/use-labels-empty-state';
import * as Styled from './styles';
import * as SX from './sx';
import type { LabelsListProps as Props } from './types';

export const LabelsList = memo(
  ({ className, type, searchType, searchValue }: Props) => {
    const {
      onSelect,
      onRemove,
      labels: selectedLabels,
      overrideLabels: contextOverrideLabels,
    } = useLabelManagementContext();
    const { tabValue, setTabValue, effectiveSearchType } =
      useTableActionContext();
    const [sortedItems, setSortedItems] = useState<Label[]>([]);

    // Prefer the explicit prop, then the action context's effective type (which
    // already folds in the ActionButton override and the page route). This keeps
    // the person's lists/labels showing when opened from the employees sidebar on
    // a company search page, instead of falling back to the company grouping.
    const grouping = useMemo(
      () =>
        (searchType ?? effectiveSearchType) === SearchType.People
          ? LabelGrouping.PEOPLE
          : LabelGrouping.COMPANY,
      [searchType, effectiveSearchType]
    );
    const allLabelsData = useBootstrapLabels(type, grouping);
    const { items: allItems } = allLabelsData;

    // Mine / Team split on the creator, not on the item's visibility, so a list
    // the user created and shared still shows under "Mine" (with its lock icon).
    const scopedItems = contextOverrideLabels ?? allItems;
    const counts = useOwnershipTabCounts(scopedItems);
    const filterByOwnershipTab = useFilterLabelsByOwnershipTab();

    const filteredItems = useMemo(() => {
      let items = filterByOwnershipTab(scopedItems, tabValue);

      if (searchValue?.trim()) {
        const searchLower = searchValue.toLowerCase().trim();
        items = items.filter((item) =>
          item.name.toLowerCase().includes(searchLower)
        );
      }

      return items;
    }, [scopedItems, tabValue, searchValue, filterByOwnershipTab]);

    useEffect(() => {
      const selectedIds = new Set(
        selectedLabels?.map((label) => label.id) ?? []
      );

      const sorted = [...filteredItems].sort((a, b) => {
        const aIsSelected = selectedIds.has(a.id);
        const bIsSelected = selectedIds.has(b.id);

        if (aIsSelected && !bIsSelected) return -1;
        if (!aIsSelected && bIsSelected) return 1;
        return 0;
      });

      setSortedItems(sorted);
    }, [filteredItems]);

    const { isLoading } = allLabelsData;
    const { isEmpty, emptyMessage } = useLabelsEmptyState({
      counts,
      filteredItemsLength: filteredItems.length,
      tabValue,
      searchValue,
      type,
    });

    const handleLabelChange = (checked: boolean, label: Label) => {
      if (checked) {
        const updatedLabels = [...(selectedLabels ?? []), label];
        onSelect(updatedLabels);
      } else {
        onRemove(label.id);
      }
    };

    const handleChangeTab = (
      event: React.SyntheticEvent,
      newValue: PresetVisibility
    ) => {
      event.stopPropagation();
      setTabValue(newValue);
    };

    return (
      <Styled.Wrapper
        className={classnames('LabelsList', className)}
        sx={SX.root}
      >
        <CardContent padding="normal" sx={SX.cardContent}>
          <VisibilityTabs
            tabValue={tabValue}
            counts={counts}
            onChange={handleChangeTab}
            isLoading={isLoading}
          />

          {isEmpty ? (
            <Stack sx={SX.dataNotFoundWrapper}>
              <Typography variant="body4" color="grey.400" fontWeight={500}>
                {emptyMessage}
              </Typography>
            </Stack>
          ) : (
            <Scrollbar sx={SX.scrollbarSX}>
              <Stack gap={0.5}>
                {sortedItems?.map((label) => {
                  const isChecked = selectedLabels?.some(
                    (item) => item.id === label.id
                  );

                  return (
                    <LabelsListItem
                      key={label.id}
                      label={label}
                      checked={isChecked ?? false}
                      onChange={handleLabelChange}
                    />
                  );
                })}
              </Stack>
            </Scrollbar>
          )}
        </CardContent>
      </Styled.Wrapper>
    );
  }
);

LabelsList.displayName = 'LabelsList';
